import { useRef, useState } from "react";
import { Camera } from "lucide-react";

interface Props {
  onSubmit: (input: { imageDataUrl: string; caption: string }) => void;
  busy: boolean;
}

export default function PhotoCapture({ onSubmit, busy }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState("");

  function pickPhoto() {
    fileInput.current?.click();
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await downscaleImage(file, 800);
    setPreview(dataUrl);
  }

  function submit() {
    if (!preview) return;
    onSubmit({ imageDataUrl: preview, caption: caption.trim() });
  }

  function reset() {
    setPreview(null);
    setCaption("");
    if (fileInput.current) fileInput.current.value = "";
  }

  return (
    <div className="p-4 space-y-4">
      {preview ? (
        <img src={preview} alt="Meal preview" className="w-full rounded-lg border border-slate-800" />
      ) : (
        <>
          <button
            onClick={pickPhoto}
            className="w-full aspect-square rounded-lg border-2 border-dashed border-slate-700 flex flex-col items-center justify-center text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors gap-2"
          >
            <Camera className="w-12 h-12" strokeWidth={1.5} />
            <span>Take or choose photo</span>
            <span className="text-xs text-slate-500 mt-1">From camera or photo library</span>
          </button>
          <p className="text-xs text-slate-400 text-center">
            Camera denied? You can pick from your photo library instead — the system picker handles both.
          </p>
        </>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
      />

      <label className="block">
        <span className="text-sm text-slate-400">Optional caption</span>
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder='e.g. "homemade pasta, used olive oil"'
          rows={2}
          className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
        />
      </label>

      <div className="flex gap-2">
        {preview && (
          <button
            onClick={reset}
            disabled={busy}
            className="flex-1 py-3 rounded-lg border border-slate-700 text-slate-300 disabled:opacity-50"
          >
            Retake
          </button>
        )}
        <button
          onClick={submit}
          disabled={!preview || busy}
          className="flex-1 py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-40"
        >
          {busy ? "Analyzing..." : "Estimate calories"}
        </button>
      </div>
    </div>
  );
}

async function downscaleImage(file: File, maxDim: number): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, w, h);
  // 800px @ 0.75 quality is enough for AI vision + thumbnails. ~50% smaller
  // than the previous 1024px @ 0.85 — faster uploads, less Storage spend.
  return canvas.toDataURL("image/jpeg", 0.75);
}
